"use client";

import { ErrorBoundary } from "@/app/components/shared/error-boundary";
import type { Locale } from "@/domain";
import Script from "next/script";

import * as styles from "./recommended-products-element.module.scss";

export type RecommendationSource = "PDP" | "ATC" | "CONSUMER_CART";

export interface RecommendedProductsElementProps {
	/** Parent SKU — drives the upstream recommendation call. */
	sku: string;
	/** Locale string the bundle's recommendation service expects. */
	locale: Locale;
	/** Recommendation surface — defaults to `PDP`. */
	source?: RecommendationSource;
	/** Presentation variant — `compact` renders the ATC cross-sell layout. */
	variant?: "default" | "compact";
	/** Optional heading override (e.g. "Frequently bought together"). */
	title?: string;
}

/**
 * Renders the shared `<recommended-products>` web component shipped by the
 * `consumer-web-elements` bundle. The bundle self-registers the custom
 * element on load and fetches recommendations via its internal
 * `fetchRecommendations(locale, source, sku)` call.
 *
 * `/consumer-web-elements.js` is served from S3 + Cloudflare in prod and
 * proxied to sandbox under `next dev` (see `next.config.js`). Custom-element
 * JSX typings live in `src/types/custom-elements.d.ts`.
 *
 * Wrapped in an `ErrorBoundary` so a bundle-load failure (e.g. CDN miss)
 * never blocks the rest of the PDP from rendering.
 */
export function RecommendedProductsElement({
	sku,
	locale,
	source = "PDP",
	variant = "default",
	title
}: RecommendedProductsElementProps): JSX.Element {
	return (
		<ErrorBoundary componentId="recommended_products_element">
			<Script src="/consumer-web-elements.js" strategy="lazyOnload" />
			<div
				className={[
					styles["wrapper"],
					variant === "compact" && styles["wrapper--compact"]
				]
					.filter(Boolean)
					.join(" ")}
				data-component="RecommendedProductsElement"
			>
				<recommended-products
					sku={sku}
					locale={locale}
					source={source}
					variant={variant}
					{...(title ? { title } : {})}
				/>
			</div>
		</ErrorBoundary>
	);
}
